import type { DataLayer } from "./dataLayer/DataLayer";

/**
 * Minimal shape of Capacitor's App plugin (`@capacitor/app`) as exposed on the
 * runtime bridge. The web bundle has no Capacitor npm dependency — it talks to
 * whatever the native shell injects on `window.Capacitor`, exactly like the
 * Apple sign-in path does — so we feature-detect and type it locally.
 */
interface CapacitorAppPlugin {
  addListener?: (
    eventName: "appUrlOpen" | "appStateChange",
    listener: (data: { url?: string; isActive?: boolean }) => void,
  ) => Promise<unknown>;
  getLaunchUrl?: () => Promise<{ url: string } | null | undefined>;
}

interface CapacitorRuntime {
  Plugins?: { App?: CapacitorAppPlugin };
}

/** Pull a friend-invite code out of a URL's `?invite=` query, if valid. */
function inviteCodeFromUrl(rawUrl: string): string | null {
  try {
    const code = new URL(rawUrl).searchParams.get("invite");
    return code && /^[A-Za-z0-9_-]{16,64}$/.test(code) ? code : null;
  } catch {
    return null;
  }
}

function hasPatreonCallback(rawUrl: string): boolean {
  try {
    return new URL(rawUrl).searchParams.has("patreon");
  } catch {
    return false;
  }
}

/**
 * Wire native iOS Universal Links to friend-invite and Patreon callback flows.
 *
 * The native shell loads the remote PWA (`server.url = https://beta.wirdergame.com`),
 * so when a user taps a Universal Link like `https://beta.wirdergame.com/?invite=CODE`,
 * iOS opens the app instead of Safari — but the code never lands in
 * `window.location` (the web view is already showing the configured server URL).
 * Capacitor delivers the tapped URL through the App plugin's `appUrlOpen` event
 * instead, plus `getLaunchUrl()` for the cold-start case where the event can fire
 * before this listener attaches. Both funnel to the same pending-invite handling
 * the web path uses.
 *
 * No-op in a plain browser / PWA (the App plugin isn't present), so non-native
 * behaviour is completely unchanged.
 */
export function registerNativeDeepLinkHandler(dataLayer: DataLayer): void {
  const cap = (window as unknown as { Capacitor?: CapacitorRuntime }).Capacitor;
  const app = cap?.Plugins?.App;
  if (!app) return;

  const handledUrls = new Set<string>();
  const handleUrl = (rawUrl: string): void => {
    if (!rawUrl || handledUrls.has(rawUrl)) return;
    handledUrls.add(rawUrl);
    if (hasPatreonCallback(rawUrl)) {
      void dataLayer.handleIncomingPatreonCallback(rawUrl);
      return;
    }
    const code = inviteCodeFromUrl(rawUrl);
    if (code) {
      void dataLayer.handleIncomingInvite(code);
      return;
    }
  };

  app.addListener?.("appUrlOpen", (data) => {
    handleUrl(data?.url ?? "");
  });

  app
    .getLaunchUrl?.()
    .then((res) => {
      if (res?.url) handleUrl(res.url);
    })
    .catch(() => {
      /* getLaunchUrl is best-effort; the appUrlOpen listener is the primary path. */
    });
}

/** Refresh authenticated membership when a native app becomes active again. */
export function registerNativeAppResumeHandler(dataLayer: DataLayer): void {
  const cap = (window as unknown as { Capacitor?: CapacitorRuntime }).Capacitor;
  const app = cap?.Plugins?.App;
  if (!app) return;

  app.addListener?.("appStateChange", (state) => {
    if (state?.isActive) void dataLayer.handleAppResume();
  });
}
